import { useCallback, useEffect, useRef, useState } from "react";

import { snapToPrecision as snapToPrecisionUtil } from "@/components/ui/slider-utils";

import { formatDollarLongHand } from "@/lib/format";
import { cn } from "@/lib/utils";

import { Input } from "./input";

interface CurrencyInputProps {
  value: number | null;
  min?: number;
  max?: number;
  precision?: number;
  onChange: (value: number | null) => void;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  /** When set, Enter runs this after normalizing the value instead of submitting the nearest form. */
  onEnter?: () => void;
}

const CurrencyInput = ({
  value,
  min,
  max,
  precision,
  onChange,
  className,
  required,
  disabled,
  onEnter,
}: CurrencyInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Input value is treated as a string.
  // That allows the user to do things like type in "-" or "0.", without it casting that to 0.
  const [strValue, setStrValue] = useState<string>(
    value !== null ? value.toString() : "",
  );

  // Call this after blur. It should never get in the way of the user typing.
  const formatValueForDisplay = useCallback((value: number | null): string => {
    return value !== null ? formatDollarLongHand(value) : "";
  }, []);

  // Keep display in sync when the parent updates `value` (e.g. reset, apply defaults).
  useEffect(() => {
    setStrValue(formatValueForDisplay(value));
  }, [value, formatValueForDisplay]);

  const snapToPrecision = useCallback(
    (manualValue: number) => {
      return snapToPrecisionUtil(
        manualValue,
        min || -9999999999,
        max || 9999999999,
        precision || 0.01,
      );
    },
    [min, max, precision],
  );

  const onBlur = (manualValue: number | null) => {
    if (manualValue === null) {
      onChange(null);
      return;
    }

    onChange(snapToPrecision(manualValue));
  };

  // We never want to show a NaN if they type in a bad value.
  // If that happens, we fall back to the previous value.
  // If the input is empty, we return null to represent a blank state.
  const cleanValue = (numString: string): number | null => {
    // If the input is empty or just whitespace, return null
    if (!numString.trim()) {
      return null;
    }

    const cleanedStr = numString
      .replaceAll("%", "")
      .replaceAll("$", "")
      .replaceAll(",", "");

    const cleanedNum = Number(cleanedStr);

    // If not a valid number, fall back to previous value (which could be null)
    const validNum = Number.isNaN(cleanedNum) ? value : cleanedNum;

    // Only snap to precision if we have a valid number
    return validNum !== null ? snapToPrecision(validNum) : null;
  };

  const cleanAndFormat = () => {
    const cleanedValue = cleanValue(strValue);
    const formattedValue = formatValueForDisplay(cleanedValue);
    setStrValue(formattedValue);
    onBlur(cleanedValue);
  };

  return (
    <Input
      ref={inputRef}
      className={cn("w-full font-mono", className)}
      type="currency"
      value={strValue}
      min="-9999999"
      onChange={(e) => {
        setStrValue(e.target.value);
      }}
      onBlur={cleanAndFormat}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          // On blur, we clean up and format the value neatly. We want to do the same thing on enter.
          e.preventDefault();
          cleanAndFormat();
          if (onEnter) {
            setTimeout(() => onEnter(), 0);
            return;
          }
          const form = inputRef.current?.closest("form");
          if (form) {
            // cleanAndFormat calls onChange, which schedules parent setState. requestSubmit runs
            // onSubmit in the same synchronous turn, before React applies that update — so the form
            // would read stale state (e.g. F1099-DIV box 1a). Defer submit until after commit.
            setTimeout(() => form.requestSubmit(), 0);
          }
        }
      }}
      required={required}
      disabled={disabled}
    />
  );
};

export { CurrencyInput };
